import React, { useState, useEffect, useContext, useRef } from 'react'
import { Link } from 'react-router-dom'
import useStyles from './styles'
import clsx from 'clsx'
import {
  View,
  ViewContext,
  SplitView,
  SplitViewPane,
  Text,
  Table,
  CardList,
  Board,
  BoardVirtualized,
  FilterBar,
  Button,
  MenuPopover,
  InputField,
  CalendarView,
  AgendaList,
  Tabs,
  Tab,
} from "shared/ui"
import {
  Paper,
  TableContainer,
  useMediaQuery,
  useTheme,
  Badge as MuiBadge,
  Box,
  Tooltip,
  IconButton,
  CircularProgress,
} from '@material-ui/core'
import Skeleton from '@material-ui/lab/Skeleton'
import Pagination from '@material-ui/lab/Pagination'
import {
  Add as AddIcon,
  ArrowBackIos as BackIcon,
  MoreVert as MoreIcon,
  VerticalSplit as SplitViewIcon,
  TableChart as TableViewIcon,
  Sort as SortIcon,
  Refresh as RefreshIcon,
  ExpandMore
} from '@material-ui/icons'
import getChannelType from "fixtures/channelTypes"
import CustomSortDialog from './CustomSortDialog'

function ListViewController(props) {
  const { loadMoreData, refreshData, isFetching, lastFetched, data } = props
  const { selfLink, selectedKey, selectedIndex, selectedEvent, renderDetailView } = props
  const { primaryFilterFields, secondaryFilterFields } = props
  const { filterButton, tableProps = {} } = props
  const { filterData, onFilterDataChange } = props
  const { renderRecordCount, renderEmptyAction, showEmptyState = false } = props
  // `showRefreshButton`: a small grey reload icon button after the record count that calls `refreshData`; while the list is
  // fetching it becomes a grey spinner.
  const { showRefreshButton = false } = props
  const { tableColumns, boardColumns, renderCardItem, cardFields, onItemClick, onMoveItem, calendarFields } = props
  // Calendar-only: an alternate date-grouped "Agenda" body for viewStyle="calendar".
  // agendaMode swaps <CalendarView> for <AgendaList>; extraBreadcrumbsContent lets the
  // page drop a control (e.g. the Monthly/Agenda switch) into the header's breadcrumb row.
  const { agendaMode = false, renderAgendaItem, agendaGetDate, agendaEmptyText, agendaBucketize, agendaRenderGroupHeader, agendaHeader, agendaMaxWidth, agendaAlign, extraBreadcrumbsContent } = props
  const { agendaMaxItemsPerGroup, agendaLoadMoreDedupeKey } = props
  // "Today" handling — see AgendaList's currentGroupKey / scrollRequest / scrollReady.
  const { agendaCurrentGroupKey, agendaEmptyGroupText, agendaScrollRequest, agendaScrollReady } = props
  // Calendar-only, opt-in controlled navigation: when the page owns its own month state
  // (apps/calendar's CalendarPage) it passes `currentMonth`/`onNavigate` through to
  // CalendarView (fully controlled, no internal toolbar) and `calendarHeaderActions`
  // (e.g. Refresh/Back/Today/Next) into the View header's title row. Every other
  // viewStyle="calendar" caller omits all of these and is unaffected.
  const { currentMonth, onNavigate, calendarHeaderActions, secondaryFilterTrailingContent } = props
  const { showColumnHeader = false } = props
  const { enableSelection = false, enableIndicatorSelected = false, selectedCount = 0, isItemSelected, onSelectAll, onSelectItem } = props
  const { primaryButton, secondaryButtons, selectionButtons, showPrimaryButtonLast = false } = props
  const { sortFieldOptions, sortFieldValue, onSortFieldChange, useCustomSortFields = false, customSortFields = [] } = props
  const [viewStyle, setViewStyle] = useState(props.defaultViewStyle || "table")
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null)
  const [openMenu, setOpenMenu] = useState(false)
  const [scrollType, setScrollType] = useState("next")
  const [showCustomSort, setShowCustomSort] = useState(false)
  const [customSortOption, setCustomSortOption] = useState({ value: 'customsort', text: 'Custom', isCustom: true })
  const [headerHeight, setHeaderHeight] = useState(0)
  const viewContext = useContext(ViewContext)
  const { useHeader, isInModal = false, forceUseSideToggle = false, enableTableScrolling = false, enableTablePagination = false, showGroupBy = false, groupByFields = [], onToggleGroupBy } = props
  const { paginationCount, paginationPage, paginationOnChange } = tableProps

  const classes = useStyles()
  const theme = useTheme()
  const smDown = useMediaQuery(theme.breakpoints.down("sm"))

  const useCardList = smDown || viewStyle === "split"

  const headerRef = useRef(0)
  const agendaStickyRef = useRef(null)
  const itemListRef = useRef([])
  const groupByRef = useRef()
  const containerRef = useRef()
  const tableContainerRef = useRef(null)

  const [isFetchingLoadMore, setIsFetchingLoadMore] = useState(false)

  useEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.clientHeight)
    }
  }, [])

  useEffect(() => {
    if (!isFetching) setIsFetchingLoadMore(false)
  }, [isFetching])

  let actionButtons = []
  if (viewStyle === "split" || smDown) {
    if (primaryButton) {
      actionButtons.push({
        showLabel: false,
        color: "primary",
        ...primaryButton,
      })
    }
    if (secondaryButtons)
      actionButtons = showPrimaryButtonLast ? [...secondaryButtons, ...actionButtons] : [...actionButtons, ...secondaryButtons]
  }
  else {
    if (primaryButton) {
      actionButtons.push({
        variant: "contained",
        color: "primary",
        ...primaryButton,
      })
    }

    if (secondaryButtons)
      actionButtons = showPrimaryButtonLast ? [...secondaryButtons, ...actionButtons] : [...actionButtons, ...secondaryButtons]
  }

  // Add view style button
  let viewStyles = []
  if (props.allowedViewStyles)
    viewStyles = props.allowedViewStyles
  else {
    if (tableColumns)
      viewStyles.push("table")
    if (props.defaultViewStyle === "board")
      viewStyles.push("board")
    else if (cardFields || renderCardItem)
      viewStyles.push("split")
  }

  if (viewStyles.length > 1) {
    const i = viewStyles.indexOf(viewStyle)
    const nextViewStyle = viewStyles[i + 1 >= viewStyles.length ? 0 : i + 1]
    let icon = TableViewIcon
    if (nextViewStyle === "split")
      icon = SplitViewIcon

    if (viewStyle === "split") {
      if (nextViewStyle == "table") {
        icon = TableViewIcon
      } else {
        icon = SplitViewIcon
      }
    }
    else if (viewStyle === "board")
      icon = SplitViewIcon

    actionButtons.push({
      icon,
      color: "primary",
      onClick: () => setViewStyle(nextViewStyle)
    })
  }

  if (actionButtons.length > 0 && actionButtons[actionButtons.length - 1].variant !== "contained")
    actionButtons[actionButtons.length - 1].insetRightMargin = true

  useEffect(() => {
    viewContext.setLoading(isFetching)
    return () => {
      viewContext.setLoading(false)
    }
  }, [isFetching])

  useEffect(() => {
    // reset scroll button on each closing
    if (!showGroupBy) setScrollType("next")
  }, [showGroupBy])

  useEffect(() => {
    const allSortFieldOptions = [...(sortFieldOptions || []), customSortOption]
    const findOption = allSortFieldOptions.find(field => field.value === sortFieldValue)

    if (!findOption) {
      setCustomSortOption({
        value: sortFieldValue,
        text: "Custom",
        isCustom: true
      })
    }
  }, [sortFieldValue, sortFieldOptions, customSortOption])

  const handleOnScroll = (e) => {
    let load = false
    let topFirstPage = 0

    if (!isFetching && itemListRef.current.length > 0) {
      let firstDataPage = data?.records?.[data?.records?.length - 20]
      let carditem = itemListRef.current[data?.records?.length - 20]?.getBoundingClientRect()
      topFirstPage = carditem?.top || 0
    }

    if (!load && !isFetching && topFirstPage < 0) {
      load = true
    } else if (Math.floor(Math.floor(e.target.scrollHeight) - Math.floor(e.target.scrollTop)) === Math.floor(e.target.clientHeight)) {
      // bottom
      load = true
    } else if (Math.floor(Math.floor(e.target.scrollHeight) - Math.floor(e.target.scrollTop)) === Math.floor(e.target.clientHeight) + 1) {
      // bottom
      load = true
    }

    if (load && loadMoreData) {
      loadMoreData()
      setIsFetchingLoadMore(true)
    }
  }

  /** loadMore logic only for limited-height table */
  const onLoadMore = () => {
    if(!loadMoreData) return 
    
    loadMoreData()
    setIsFetchingLoadMore(true)
  }

  const toggleMenu = (target, open) => {
    setOpenMenu(open)
    setPopoverAnchorEl(target)
  }

  const renderCardList = () => {
    if (!cardFields && !renderCardItem)
      return
    if (lastFetched)
      return (
        <div className={classes.listViewCardListInset}>
          <CardList
            fields={cardFields}
            renderCardItem={renderCardItem}
            itemListRef={itemListRef}
            items={data.records}
            selectedIndex={selectedIndex}
            isFetching={isFetching && isFetchingLoadMore}
            skeleton={isFetching && !isFetchingLoadMore ? 3 : undefined}
            onCardClick={onItemClick}
            showCheckBox={enableSelection}
            onRowSelect={onSelectItem}
            isRowSelected={isItemSelected}
          />
        </div>
      )
    else if (isFetching)
      return (
        <div className={classes.listViewCardListInset}>
          <CardList
            fields={cardFields}
            renderCardItem={renderCardItem}
            skeleton={3}
          />
        </div>
      )
  }

  const renderTable = () => {
    if (!tableColumns)
      return

    if (enableTableScrolling) {
      if (lastFetched)
        return (
          <Paper>
            <TableContainer ref={tableContainerRef} style={{ height: `calc(100vh - ${headerHeight * 3}px + 24px)`, paddingBottom: enableTablePagination ? 0 : 32 }}>
              <Table
                itemListRef={itemListRef}
                columns={tableColumns}
                rows={data.records}
                showColumnHeader={showColumnHeader}
                isFetching={isFetching && isFetchingLoadMore}
                skeleton={isFetching && !isFetchingLoadMore ? 3 : undefined}
                onRowClick={onItemClick}
                showCheckBox={enableSelection}
                showIndicatorSelected={enableIndicatorSelected}
                onRowSelect={onSelectItem}
                isRowSelected={isItemSelected}
                columnHeaderStyle={{
                  // top: headerRef?.current?.clientHeight,
                  backgroundColor: 'white'
                }}
                {...tableProps}
                containerRef={tableContainerRef}
                onLoadMore={onLoadMore}
              />
            </TableContainer>
            {enableTablePagination && paginationCount > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
                <Pagination
                  count={paginationCount}
                  page={paginationPage}
                  onChange={paginationOnChange}
                  color="secondary"
                />
              </div>
            )}
          </Paper>
        )
      else if (isFetching)
        return (
          <TableContainer>
            <Table
              columns={tableColumns}
              skeleton={3}
              showColumnHeader={showColumnHeader}
              showCheckBox={enableSelection}
              {...tableProps}
            />
          </TableContainer>
        )
    } else {
      if (lastFetched)
        return (
          <div className={classes.listViewTableInset}>
            <Table
              itemListRef={itemListRef}
              columns={tableColumns}
              rows={data.records}
              showColumnHeader={showColumnHeader}
              isFetching={isFetching && isFetchingLoadMore}
              skeleton={isFetching && !isFetchingLoadMore ? 3 : undefined}
              onRowClick={onItemClick}
              showCheckBox={enableSelection}
              showIndicatorSelected={enableIndicatorSelected}
              onRowSelect={onSelectItem}
              isRowSelected={isItemSelected}
              columnHeaderStyle={{
                top: headerRef?.current?.clientHeight,
                backgroundColor: 'white'
              }}
              {...tableProps}
            />
          </div>
        )
      else if (isFetching)
        return (
          <div className={classes.listViewTableInset}>
            <Table
              columns={tableColumns}
              skeleton={3}
              showColumnHeader={showColumnHeader}
              showCheckBox={enableSelection}
              {...tableProps}
            />
          </div>
        )
    }
  }


  const renderBoard = () => {
    if (!boardColumns)
      return
    if (lastFetched) {
      return (
        <div className={classes.listViewBoardInset}>
          <Board
            columns={boardColumns}
            cardFields={cardFields}
            renderCardItem={renderCardItem}
            items={data.records}
            selectedIndex={selectedIndex}
            isFetching={isFetching}
            onCardClick={onItemClick}
            onMoveItem={onMoveItem}
            itemListRef={itemListRef}
          />
        </div>
      )
    }
    // else if (isFetching)
    //   return (
    //     <div className={classes.listViewBoardInset}>
    //       <Board
    //         columns={boardColumns}
    //         cardFields={cardFields}
    //         renderCardItem={renderCardItem}
    //         skeleton={3}
    //         />
    //     </div>
    //   )
  }

  const renderCalendar = () => {
    if (lastFetched) {
      return (
        <CalendarView
          calendarFields={calendarFields}
          renderCardItem={renderCardItem}
          onCardClick={onItemClick}
          items={data.records}
          selectedIndex={selectedIndex}
          selectedEvent={selectedEvent}
          isFetching={isFetching}
          containerRef={containerRef}
          filterData={filterData}
          onCalendarDayClick={props?.onCalendarDayClick}
          onCalendarMonthChange={props?.onCalendarMonthChange}
          eventColorFor={props?.eventColorFor}
          eventAppearanceFor={props?.eventAppearanceFor}
          currentMonth={currentMonth}
          onNavigate={onNavigate}
          refreshData={refreshData}
        />
      )
    }
  }

  // has_more from the newest page if the endpoint sends it (calendar reducers keep it
  // across appends); otherwise derive from total vs how many rows are loaded. Never
  // "more" when we can't tell — avoids a sentinel that re-fires forever.
  const agendaHasMore = () => {
    if (!data) return false
    if (data.has_more === true) return true
    if (data.has_more === false) return false
    if (typeof data.total !== 'number') return false
    const loaded = (data.offset || 0) + (typeof data.size === 'number' ? data.size : (data.records?.length || 0))
    return data.total > loaded
  }

  // Room the auto-scroll must leave for the sticky page header + the sticky filter block.
  const getAgendaScrollOffset = () => headerHeight + (agendaStickyRef.current?.offsetHeight || 0)

  const renderAgenda = () => {
    if (lastFetched)
      return (
        <AgendaList
          items={data.records}
          getDate={agendaGetDate}
          renderItem={renderAgendaItem}
          bucketize={agendaBucketize}
          renderGroupHeader={agendaRenderGroupHeader}
          maxWidth={agendaMaxWidth}
          align={agendaAlign}
          maxItemsPerGroup={agendaMaxItemsPerGroup}
          currentGroupKey={agendaCurrentGroupKey}
          emptyGroupText={agendaEmptyGroupText}
          scrollRequest={agendaScrollRequest}
          scrollReady={agendaScrollReady}
          getScrollOffset={getAgendaScrollOffset}
          isFetching={isFetching}
          emptyText={agendaEmptyText}
          onLoadMore={loadMoreData ? onLoadMore : undefined}
          hasMore={agendaHasMore()}
          loadingMore={isFetching && isFetchingLoadMore}
          loadMoreDedupeKey={agendaLoadMoreDedupeKey}
        />
      )
    if (isFetching)
      return <Text color="textMuted" style={{ padding: 24, display: 'block' }}>Loading…</Text>
  }

  const renderFilterBar = () => {
    if (primaryFilterFields || secondaryFilterFields)
      return (
        <div className={clsx(
          classes.listViewFilterBar,
          useCardList && classes.listViewFilterBarCardInset,
        )}>
          <FilterBar
            primaryFields={primaryFilterFields}
            secondaryFields={secondaryFilterFields}
            filterData={filterData}
            onFilterDataChange={onFilterDataChange}
            secondaryTrailingContent={secondaryFilterTrailingContent}
          />
        </div>
      )
  }

  const renderFilterButton = () => {
    if (filterButton)
      return (
        <div className={classes.listViewFilterButton}>
          {filterButton.map(item =>
            <Button badge={item.badge} size="small">
              {item.name}
            </Button>
          )}
        </div>
      )
  }

  const renderNavigationBar = () => {
    let selectedSortField
    if (sortFieldOptions && sortFieldOptions.length > 0)
      selectedSortField = sortFieldOptions.find(field => field.name === sortFieldValue) || sortFieldOptions[0]

    if (props?.disableNavigationBar) return null

    const allSortFieldOptions = [...(sortFieldOptions || [])]

    if (useCustomSortFields && customSortFields.length > 0) {
      allSortFieldOptions.push(customSortOption)
    }

    return (
      <div className={classes.listViewNavigationBar}>
        {lastFetched && selectedCount <= 0 &&
          <Text variant="body2" color="textMuted">
            {renderRecordCount ? renderRecordCount(data) :
              <>
                {data.total} records
              </>
            }
          </Text>
        }
        {showRefreshButton && refreshData && lastFetched && selectedCount <= 0 &&
          (isFetching ? (
            <div style={{ width: 24, height: 24, marginLeft: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <CircularProgress size={14} thickness={5} style={{ color: '#9e9e9e' }} />
            </div>
          ) : (
            <Tooltip title="Refresh">
              <IconButton size="small" aria-label="Refresh" style={{ marginLeft: 4, padding: 4 }} onClick={() => refreshData()}>
                <RefreshIcon style={{ fontSize: 16, color: '#9e9e9e' }} />
              </IconButton>
            </Tooltip>
          ))
        }
        {lastFetched && selectedCount > 0 &&
          <>
            <Text variant="body2" color="textPrimary">
              {selectedCount} of {data.total} records selected
            </Text>
            <div className={classes.listViewNavigationBarItemSpacing} />
            <Button variant="text" color="primary" size="small"
              onClick={() => onSelectAll(true)}
            >
              Select All
            </Button>
            <Button variant="text" color="primary" size="small"
              onClick={() => onSelectAll(false)}
            >
              Clear Selection
            </Button>
          </>
        }
        {!lastFetched && isFetching &&
          <Text variant="body2" color="textMuted">
            <Skeleton />
          </Text>
        }
        <div style={{ flexGrow: 1 }} />
        {lastFetched && selectedCount > 0 ?
          selectionButtons && selectionButtons.map(button => (
            <Button
              size="small"
              variant="contained"
              {...button}
            />
          )).reduce((p, n) =>
            p ?
              [...p, <div className={classes.listViewNavigationBarItemSpacing} />, n]
              : [n], null
          )
          // : selectedSortField &&
          //   <>
          //     <Button kind="sort" label={selectedSortField.label} size="small" insetRightMargin={true} />          
          //   </>
          : sortFieldOptions && sortFieldOptions.length > 0 &&
          <InputField
            type="option"
            options={allSortFieldOptions}
            value={sortFieldValue}
            variant="clean"
            fullWidth={false}
            size="small"
            icon={SortIcon}
            hideOptionArrow={true}
            onChange={v => {
              if (
                (typeof v === 'string' && v === customSortOption.value) ||
                (typeof v === 'object' && v?.value === customSortOption.value)
              ) {
                return
              } else {
                onSortFieldChange(v)
              }
            }}
            renderCustomOption={(o) => (
              <div
                style={{ display: 'flex', flex: 1 }}
                onClick={() => { if (o?.isCustom) setShowCustomSort(true) }}>
                {o.text || o.name}
              </div>
            )}
          />
        }
      </div>
    )
  }

  const getBtnStyle = (item) => {
    if (filterData?.[showGroupBy]?.toString() === item?.value?.toString())
      return 'contained'
    else if (item?.value === '' && !filterData?.[showGroupBy])
      return 'contained'
    else return undefined
  }

  const handleGroupByScroll = (e) => {
    if (e?.target?.scrollLeft === 0) setScrollType("next")
    else if (e?.target?.scrollLeft === e?.target?.scrollWidth - e?.target?.clientWidth - 0.5)
      setScrollType("prev")
  }

  const renderCustomSortDialog = () => {
    if (showCustomSort) {
      return (
        <CustomSortDialog
          open={showCustomSort}
          onClose={() => setShowCustomSort(false)}
          customSortFields={customSortFields}
          onCustomSortChange={(value) => {
            setCustomSortOption(value)
            onSortFieldChange(value.value)
            setShowCustomSort(false)
          }}
          selectedSortField={customSortOption}
          sortFieldOptions={sortFieldOptions}
        />
      )
    }
  }

  if (viewStyle === "split" && !smDown)
    return (
      <>
        {showGroupBy && (
          <div style={{ overflow: 'hidden' }}>
            <div className={classes.groupByBar} ref={groupByRef} onScroll={handleGroupByScroll}>
              {/* <div className={classes.groupByTitle}>
                <Text variant="body2" style={{ fontWeight: 700 }}>
                  Group by
                </Text>
              </div>
              <div style={{ marginRight: 4 }}>
                <Button
                  size="small"
                  disableElevation
                  onClick={e => toggleMenu(e.target, true)}
                >
                  {groupByFields?.find(g => g?.name === showGroupBy)?.placeholder}
                  <ExpandMore style={{ marginLeft: 4 }} />
                </Button>
              </div> */}
              <div style={{ marginRight: 4 }}>
                <Tooltip title="Remove grouping">
                  <span style={{ display: 'inline-flex' }}>
                    <Button
                      size="small"
                      disableElevation
                      onClick={onToggleGroupBy}
                      kind="close"
                    />
                  </span>
                </Tooltip>
              </div>
              <MenuPopover
                open={openMenu}
                onClose={() => toggleMenu(null, false)}
                anchorEl={popoverAnchorEl}
                menuItems={groupByFields?.map(g => ({
                  label: g.placeholder,
                  onClick: () => onFilterDataChange('groupBy', g.name)
                }))}
              />
              {showGroupBy && showGroupBy !== 'none' && (
                <>
                  {groupByFields?.find(g => g?.name === showGroupBy)?.options?.map((o, i) => (
                    <div style={{ marginRight: 4 }}>
                      <Button
                        variant={getBtnStyle(o)}
                        key={i}
                        size="small"
                        disableElevation
                        onClick={() => onFilterDataChange(showGroupBy, o.value)}
                      >
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                          {showGroupBy && showGroupBy === 'account_type' && o?.value?.length > 0 && (
                            <img src={getChannelType(o?.value, o?.text).image}
                              className={classes.groupChannelIcon}
                            />
                          )}
                          {/* {showGroupBy && showGroupBy === 'channel' && o?.value?.length > 0 && (
                            <>
                              {o?.icon_url ? (
                                <img src={o?.icon_url}
                                  className={classes.groupChannelIcon}
                                />
                              ) : (
                                <img src={getChannelType(o?.code, o?.acc_name).image}
                                  className={classes.groupChannelIcon}
                                />
                              )}
                            </>
                          )} */}
                          {showGroupBy && showGroupBy === 'channel' && o?.value?.length > 0 && (
                            <img src={getChannelType(o?.code, o?.acc_name).image}
                              className={classes.groupChannelIcon}
                            />
                          )}
                          {showGroupBy && Boolean(o?.icon) && o.icon}
                          {o?.text}
                          {o?.count ? (
                            <div className={classes.badgeDiv}>
                              <Text variant="caption" color="textSecondaryContrast">{o?.count}</Text>
                            </div>
                          ) : null}
                        </div>
                      </Button>
                    </div>
                  ))}
                  {groupByRef?.current?.scrollWidth > groupByRef?.current?.clientWidth ? (
                    <div className={classes.groupByBarReserveSpace} />
                  ) : null}
                </>
              )}
            </div>
            {groupByRef?.current?.scrollWidth > groupByRef?.current?.clientWidth ? (
              <div className={classes.groupByBarScrollBtn}>
                <Button
                  variant="contained"
                  size="small"
                  kind={scrollType}
                  onClick={() => groupByRef.current.scrollLeft += (scrollType === "next" ? 100 : -100)}
                />
              </div>
            ) : null}
          </div>
        )}
        <SplitView title={props.title} fixedPosition={true} additionalInset={showGroupBy ? 55 : 0}>
          <SplitViewPane pane="side" width="large" fixedPosition={true}
            additionalInset={showGroupBy ? 55 : 0}
            onScroll={handleOnScroll}
          >
            <View
              title={props.title}
              extraBreadcrumbsContent={extraBreadcrumbsContent}
              actionButtons={actionButtons}
            >
              {renderFilterBar()}
              {renderFilterButton()}
              {renderNavigationBar()}
              {renderCardList()}
            </View>
          </SplitViewPane>
          <SplitViewPane pane="content" fixedPosition={true} additionalInset={showGroupBy ? 55 : 0}>
            <ViewContext.Provider value={{
              ...viewContext,
              breadcrumbs: []
            }}>
              {/*
                Always add content pane eventhough selectedKey is null
                to add divider line
              */}
              {selectedKey && renderDetailView(selectedKey)}
            </ViewContext.Provider>
          </SplitViewPane>
        </SplitView>
        {renderCustomSortDialog()}
      </>
    )
  else if (viewStyle === "board")
    return (
      <ViewContext.Provider value={{
        ...viewContext,
        breadcrumbs: (smDown && selectedKey) ? [
          {
            title: smDown ? '' : props.title,
            linkTo: selfLink,
            icon: BackIcon,
            insetLeftMargin: true,
          }
        ] : forceUseSideToggle ? [...viewContext.breadcrumbs,] : [],
        // // Disable show/hide menu bar on list/detail view mode
        // breadcrumbs: [],
      }}>
        {
          (smDown && selectedKey) ?
            renderDetailView(selectedKey) :
            <View
              title={props.title}
              extraBreadcrumbsContent={extraBreadcrumbsContent}
              actionButtons={actionButtons}
              onScroll={handleOnScroll}
            >
              {renderFilterBar()}
              {renderFilterButton()}
              {renderNavigationBar()}
              {renderBoard()}
            </View>
        }
        {renderCustomSortDialog()}
      </ViewContext.Provider>
    )
  else if (viewStyle === "calendar")
    return (
      <ViewContext.Provider value={{
        ...viewContext,
        breadcrumbs: (smDown && selectedKey) ? [
          {
            title: smDown ? '' : props.title,
            linkTo: selfLink,
            icon: BackIcon,
            insetLeftMargin: true,
          }
        ] : forceUseSideToggle ? [...viewContext.breadcrumbs,] : [],
      }}>
        {
          (smDown && selectedKey) ?
            renderDetailView(selectedKey) :
            <View
              title={props.title}
              actionButtons={actionButtons}
              extraBreadcrumbsContent={extraBreadcrumbsContent}
              headerProps={{ sideContent: calendarHeaderActions, ...(props.headerProps || {}) }}
              headerRef={headerRef}
              onScroll={handleOnScroll}
              viewRef={agendaMode ? undefined : containerRef}
              viewStyle={agendaMode ? undefined : { height: '100%' }}
            >
              {agendaMode ? (
                // Agenda's own header (e.g. TaskPage's tabs) + filter bar (+ its
                // trailing Monthly/Agenda switcher) stay pinned right under the page's
                // own sticky title header while the list scrolls beneath both — same
                // sticky mechanism the table view already uses `headerRef`/`headerHeight`
                // for, just stacked one level down instead of at the very top.
                <div ref={agendaStickyRef} style={{ position: 'sticky', top: headerHeight, zIndex: 1, backgroundColor: '#fff' }}>
                  {agendaHeader}
                  {renderFilterBar()}
                  {renderFilterButton()}
                </div>
              ) : (
                <>
                  {renderFilterBar()}
                  {renderFilterButton()}
                </>
              )}
              {agendaMode ? renderAgenda() : renderCalendar()}
            </View>
        }
        {renderCustomSortDialog()}
      </ViewContext.Provider>
    )
  else if (viewStyle === "cardList")
    return (
      <ViewContext.Provider value={{
        ...viewContext,
        // Disable show/hide menu bar on list/detail view mode
        breadcrumbs: forceUseSideToggle ? [...viewContext.breadcrumbs,] : [],
      }}>
        <View
          title={props.title}
          extraBreadcrumbsContent={extraBreadcrumbsContent}
          actionButtons={actionButtons}
          useHeader={useHeader}
          fixedPosition={true}
          isInModal={isInModal}
          onScroll={handleOnScroll}
        >
          {renderFilterBar()}
          {renderFilterButton()}
          {renderNavigationBar()}
          {renderCardList()}
          {showEmptyState && data?.records?.length < 1 && !isFetching && renderEmptyAction && renderEmptyAction()}
        </View>
        {renderCustomSortDialog()}
      </ViewContext.Provider>
    )
  else if (selectedKey)
    return (
      <div>
        <ViewContext.Provider value={{
          ...viewContext,
          breadcrumbs: [
            // Disable show/hide menu bar on list/detail view mode
            // ...viewContext.breadcrumbs,
            {
              title: smDown ? '' : props.title,
              linkTo: selfLink,
              icon: BackIcon,
              insetLeftMargin: true,
            }
          ]
        }}>
          {renderDetailView(selectedKey)}
          {renderCustomSortDialog()}
        </ViewContext.Provider>
      </div>
    )
  else {
    return (
      <ViewContext.Provider value={{
        ...viewContext,
        // Disable show/hide menu bar on list/detail view mode
        breadcrumbs: forceUseSideToggle ? [...viewContext.breadcrumbs,] : [],
      }}>
        <View
          title={props.title}
          extraBreadcrumbsContent={extraBreadcrumbsContent}
          actionButtons={actionButtons}
          useHeader={useHeader}
          fixedPosition={true}
          isInModal={isInModal}
          onScroll={handleOnScroll}
          headerRef={headerRef}
        >
          {renderFilterBar()}
          {renderFilterButton()}
          {renderNavigationBar()}
          {smDown ? renderCardList() : renderTable()}
          {filterData?.q && data?.records?.length < 1 && !isFetching && renderEmptyAction && renderEmptyAction()}
        </View>
        {renderCustomSortDialog()}
      </ViewContext.Provider>
    )
  }
}

export default ListViewController
